
import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Mail, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { validateEmail } from "@/utils/validation";

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<{
    success: boolean;
    message?: string;
  }>;
  onToggleForm: () => void;
  loading: boolean;
}

const LoginForm = ({ onSubmit, onToggleForm, loading }: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("LoginForm: Handling submit");
    
    setError("");
    
    if (!validateEmail(email)) {
      console.log("LoginForm: Invalid email format");
      setError("Format d'email invalide");
      return;
    }

    try {
      const result = await onSubmit(email, password);
      if (!result.success) {
        setError(result.message || "Une erreur est survenue");
      }
    } catch (error) {
      setError("Une erreur est survenue lors de la connexion");
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-card text-card-foreground shadow-sm rounded-lg border">
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-semibold tracking-tight">
          Connexion
        </h2>
        <p className="text-sm text-gray-600">
          Connectez-vous à votre compte
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded bg-destructive/15 text-destructive text-sm">
            {error}
          </div>
        )}
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1">
            Email
          </label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              required
              className="pl-10"
              placeholder="jean.dupont@example.com"
              maxLength={100}
            />
            <Mail className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1">
            Mot de passe
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="pl-10"
              placeholder="••••••••"
              maxLength={100}
            />
            <Shield className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <Link to="/reset-password" className="text-sm text-primary hover:underline">
            Mot de passe oublié ?
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <span className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Connexion...
            </span>
          ) : "Se connecter"}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Ou
            </span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onToggleForm}
        >
          Créer un compte
        </Button>
      </form>
    </div>
  );
};

export default LoginForm;
